import * as React from 'react';
import styled, { css } from 'styled-components';
import { IconSpritesRegistrar, IIconSprite } from '../../icon-sprites-registrar';
import { LocalizationProvider } from '../../localization/localization-provider';
import { WatReactUxThemeProvider } from './theme-provider';
import { HostableSurface, IHostableSurfaceProps } from '../../components/hostable-surface';
import { WindowSizeProvider, GlobalEventsProvider, ResizeNotifierProvider } from '../../utils';
import { selectThemeSemantics } from './selectors';
import { CultureProvider } from '../../localization';

export interface IWatReactAppProps {
    className?: string;
    children?: React.ReactElement<any>;
    surfacePosition?: IHostableSurfaceProps['position'];
    themeName?: string;
    culture?: string;
    iconSpriteAddons?: IIconSprite[];
    genericIconSpritePath: string;
    translate?: (textToBeTranslated: string) => string;
}

const scrollStyleBase = css`
    scrollbar-color: ${selectThemeSemantics(({ colors: { scrollbar } }) => `${scrollbar.scrollHandleColor} ${scrollbar.scrollContainerColor}`)};
    scrollbar-width: thin;
    * {
        scrollbar-color: ${selectThemeSemantics(({ colors: { scrollbar } }) => `${scrollbar.scrollHandleColor} ${scrollbar.scrollContainerColor}`)};
        scrollbar-width: thin;
    }
    *::-webkit-scrollbar {
        width: ${selectThemeSemantics(({ gap }) => gap.l)};
        height: ${selectThemeSemantics(({ gap }) => gap.l)};
        border-radius: ${selectThemeSemantics(({ borderRadius }) => borderRadius.curvedLarge)};
        background-color: ${selectThemeSemantics(({ colors: { scrollbar } }) => scrollbar.scrollContainerColor)};
    }
    *::-webkit-scrollbar-thumb {
        border-radius: ${selectThemeSemantics(({ borderRadius }) => borderRadius.curvedLarge)};
        box-shadow: ${selectThemeSemantics(({ boxShadow }) => boxShadow.inner)};
        background-color: ${selectThemeSemantics(({ colors: { scrollbar } }) => scrollbar.scrollHandleColor)};
    }
`;

const AppCanvasLayer = styled.div`
    height: 100%;
    width: 100%;
    color: ${selectThemeSemantics(({ colors }) => colors.bodyTextColor)};
    font-family: ${selectThemeSemantics(({ baseFont }) => baseFont.fontFamily)};
    font-size: ${selectThemeSemantics(({ baseFont }) => baseFont.fontSize)};
    ${scrollStyleBase}
`;

export function WatReactAppContainer(props: IWatReactAppProps) {
    const { className, themeName, culture = 'en-US', translate, surfacePosition, iconSpriteAddons = [], genericIconSpritePath } = props;
    const iconSprites = React.useMemo(() => [...iconSpriteAddons, { name: 'generic', src: genericIconSpritePath }], [iconSpriteAddons, genericIconSpritePath]);
    return (
        <WatReactUxThemeProvider themeName={themeName}>
            <CultureProvider culture={culture}>
                <AppCanvasLayer className={className}>
                    <IconSpritesRegistrar iconSprites={iconSprites} />
                    <LocalizationProvider translate={translate}>
                        <GlobalEventsProvider>
                            <WindowSizeProvider>
                                <ResizeNotifierProvider>
                                    <HostableSurface position={surfacePosition}>
                                        {props.children}
                                    </HostableSurface>
                                </ResizeNotifierProvider>
                            </WindowSizeProvider>
                        </GlobalEventsProvider>
                    </LocalizationProvider>
                </AppCanvasLayer>
            </CultureProvider>
        </WatReactUxThemeProvider>
    );
}
